import { CircularProgress, Stack, Typography } from '@mui/material';
import { Box, SxProps, Theme } from '@mui/system';
import CustomAccordion from 'components/shared/custom_accordion';
import { CustomLabel } from 'components/shared';
import { getByIdInstitution } from 'api/institutions/institution.api';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { InstitutionStatusEnum } from 'common/enums';
import ContainerPainel from 'components/container_painel';
import {
  ModuleEnum,
  PermissionType,
  usePermissions,
} from 'contexts/auth/permission_context';
import { useEffect } from 'react';
import { ROUTES } from 'routes/constants';

const lineStyle: SxProps<Theme> | undefined = {
  display: 'flex',
};

const columnStyle: SxProps<Theme> | undefined = {
  width: '50%',
};

const labelStyle = {
  color: '#00000099',
  fontFamily: 'Roboto',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20.02px',
  letterSpacing: '0.17px',
  textAlign: 'left',
  mt: '16px',
};

const valueStyle = {
  fontFamily: 'Roboto',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  textAlign: 'left',
};

const InstitutionView: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasPermission(ModuleEnum.INSTITUTIONS, PermissionType.READ)) {
      navigate(ROUTES.HOME);
    }
  }, []);

  const fetchInstitution = async () => {
    const result = await getByIdInstitution(uuid as any);
    return result;
  };

  const { data } = useQuery(['fetchInstitution'], fetchInstitution, {
    keepPreviousData: true,
    retry: 1,
  });

  return (
    <ContainerPainel pagina='Instituições' showBackButton={true}>
      <Box sx={{ marginTop: '24px' }}>
        <Typography
          variant='h5'
          fontWeight={400}
          fontSize='1.5rem'
          textAlign='center'
        >
          Visualizar Instituição
        </Typography>

        <Typography
          variant='body1'
          fontWeight={400}
          fontSize='1rem'
          textAlign='center'
          mt='1.5rem'
        >
          Visualize todos os dados de Instituição no sistema.
        </Typography>
      </Box>

      {data ? (
        <Box sx={{ mx: '32px', mt: '32px' }}>
          <CustomAccordion
            accordionSummaryText={'Dados cadastrais'}
            style={{ boxShadow: 'none' }}
          >
            <Box>
              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel text='Nome Fantasia' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.fantasyName as any}
                    labelStyle={valueStyle}
                  />
                </Box>

                <Box sx={columnStyle}>
                  <CustomLabel text='CNPJ' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.identification as any}
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>

              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel text='Razão Social' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.name as any}
                    labelStyle={valueStyle}
                  />
                </Box>

                <Box sx={columnStyle}>
                  <CustomLabel text='CEP' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.address?.zipcode as any}
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>

              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel text='Endereço' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.address?.street as any}
                    labelStyle={valueStyle}
                  />
                </Box>

                <Box sx={columnStyle}>
                  <CustomLabel text='Número' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.address?.number as any}
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>

              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel text='Bairro' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.address?.district as any}
                    labelStyle={valueStyle}
                  />
                </Box>

                <Box sx={columnStyle}>
                  <CustomLabel text='Município' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.address?.municipality as any}
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>

              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel text='Email' labelStyle={labelStyle} />
                  <CustomLabel
                    text={data?.email as any}
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>

              <Box sx={lineStyle}>
                {data?.contactList?.map((contact, index) => (
                  <Box key={contact.uuid} sx={columnStyle}>
                    <CustomLabel
                      text={`Telefone ${index + 1}`}
                      labelStyle={labelStyle}
                    />
                    <CustomLabel text={contact.value} labelStyle={valueStyle} />
                  </Box>
                ))}
              </Box>
            </Box>
          </CustomAccordion>

          <CustomAccordion
            accordionSummaryText={'Dados administrativos'}
            style={{ boxShadow: 'none' }}
          >
            <Box>
              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel
                    text='Grupo da Instituição'
                    labelStyle={labelStyle}
                  />
                  <CustomLabel
                    text={data?.group?.name as any}
                    labelStyle={valueStyle}
                  />
                </Box>

                <Box sx={columnStyle}>
                  <CustomLabel
                    text='Perfil da Instituição'
                    labelStyle={labelStyle}
                  />
                  <CustomLabel
                    text={data?.profile?.name as any}
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>

              <Box sx={lineStyle}>
                <Box sx={columnStyle}>
                  <CustomLabel text='Cadastro Ativo?' labelStyle={labelStyle} />
                  <CustomLabel
                    text={
                      data?.institutionStatusList
                        ?.map((item: any) => item.status.name)
                        .includes(InstitutionStatusEnum.REGISTER_ACTIVE)
                        ? 'Sim'
                        : 'Não'
                    }
                    labelStyle={valueStyle}
                  />
                </Box>

                <Box sx={columnStyle}>
                  <CustomLabel
                    text='Bloquear Cadastro?'
                    labelStyle={labelStyle}
                  />
                  <CustomLabel
                    text={
                      data?.institutionStatusList
                        ?.map((item: any) => item.status.name)
                        .includes(InstitutionStatusEnum.REGISTER_BLOCKED)
                        ? 'Sim'
                        : 'Não'
                    }
                    labelStyle={valueStyle}
                  />
                </Box>
              </Box>
            </Box>
          </CustomAccordion>
        </Box>
      ) : (
        <Stack justifyContent='center' alignItems='center'>
          <CircularProgress />
        </Stack>
      )}
    </ContainerPainel>
  );
};

export default InstitutionView;
